import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react"
import { API_URL } from "../../utils/constants"
import { Mutex } from "async-mutex"

// Create a mutex to prevent multiple refreshes at once
const mutex = new Mutex()

// Get token from localStorage for API requests
// Checks team simulation token, test session token, and regular auth token
const getToken = () => {
  try {
    // Check for team simulation token first
    const teamToken = localStorage.getItem("token")
    if (teamToken) {
      return teamToken
    }

    // Check for test session token (Quick Test feature)
    const testSessionToken = localStorage.getItem("testSessionToken")
    if (testSessionToken) {
      return testSessionToken
    }

    // Fall back to Redux persist structure
    const auth = JSON.parse(localStorage.getItem("persist:msk:auth") || "{}")
    const user = JSON.parse(auth.user || "{}")
    return user.token || null
  } catch (e) {
    console.error("Error getting user token:", e)
    return null
  }
}

// Initialize base query with credentials to support cookies
const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers) => {
    // Add timezone header
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    headers.set("X-Timezone", timezone)

    // Get token from localStorage - this was the original approach
    const token = getToken()

    // If we have a token, include it in the Authorization header
    if (token) {
      headers.set("Authorization", `Bearer ${token}`)
    }

    return headers
  },
  credentials: "include", // Important: This allows cookies to be sent and received
})

// Check if we're using a test session token (Quick Test feature)
const isTestSessionAuth = () => {
  return !!localStorage.getItem("testSessionToken")
}

// Create enhanced base query with refresh logic
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Wait for any ongoing refresh operation to complete before proceeding
  await mutex.waitForUnlock()

  let result = await baseQuery(args, api, extraOptions)

  // If we get a 401 Unauthorized response, attempt to refresh the session
  // Skip refresh for test session tokens - they use JWT and don't support refresh
  if (result.error && result.error.status === 401 && !isTestSessionAuth()) {
    // Check if another request is already refreshing
    if (!mutex.isLocked()) {
      const release = await mutex.acquire()

      try {
        // Check if we recently attempted a refresh to avoid hammering the server
        const lastRefreshAttempt = parseInt(
          localStorage.getItem("session_refresh_attempt") || "0",
          10,
        )
        const now = Date.now()
        const MIN_RETRY_INTERVAL = 5000 // 5 seconds

        if (now - lastRefreshAttempt < MIN_RETRY_INTERVAL) {
          console.log("Skipping refresh - attempted too recently")
          return result
        }

        // Set current attempt timestamp
        localStorage.setItem("session_refresh_attempt", now.toString())
        console.log("Attempting to refresh session...")

        // Try to get a new session
        const refreshResult = await baseQuery(
          { url: "/saas/users/auth/refresh", method: "POST" },
          api,
          extraOptions,
        )

        // If refresh was successful, retry the original request and update refresh timestamp
        if (refreshResult.data) {
          console.log("Session refreshed successfully")
          localStorage.setItem("session_last_refresh", Date.now().toString())
          result = await baseQuery(args, api, extraOptions)
        } else {
          console.log("Session refresh failed")
          // If refresh failed, user needs to log in again
          // You could dispatch an action here to clear the auth state and redirect
        }
      } finally {
        // Release the mutex when done
        release()
      }
    } else {
      // If refresh is already in progress, wait for it to complete and retry
      await mutex.waitForUnlock()
      result = await baseQuery(args, api, extraOptions)
    }
  }

  return result
}

// Initialize an empty api service that we'll inject endpoints into later as needed
export const emptySplitApi = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Organization", "Simulation"],
  endpoints: () => ({}),
})
